import { StyleSheet } from 'react-native';
import React, { useCallback } from 'react';
import { ColorScheme, theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { Button, ButtonProps, SxProps, Theme, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { Spinner } from '@chakra-ui/react';
import { useAuth } from '../../module/AuthContext';
import { useDispatch } from 'react-redux';
import { toggleAuthModal } from '../../redux/authentication/action';

export interface ICustomButton {
    label: string;
    onClick?: (e: any) => void;
    disabled?: boolean;
    customStyle?: React.CSSProperties;
    textStyle?: React.CSSProperties;
    sx?: SxProps<Theme>;
    textSx?: SxProps<Theme>;
    loading?: boolean;
    withIcon?: boolean;
    customIcon?: any;
    buttonProps?: ButtonProps;
    beginningIcon?: any;
    withAuthentication?: boolean;
}
const CustomButton: React.FC<ICustomButton> = ({
    label,
    onClick,
    disabled,
    customStyle,
    textStyle,
    sx,
    loading,
    textSx,
    withIcon,
    customIcon,
    buttonProps,
    beginningIcon,
    withAuthentication,
}) => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle, buttonProps?.variant === 'text', buttonProps?.variant === 'outlined');
    const { isSignedIn } = useAuth();
    const dispatch = useDispatch();
    const handleOnClick = useCallback(
        (e: any) => {
            if (withAuthentication && !isSignedIn) {
                dispatch(toggleAuthModal(true));
            } else {
                onClick && onClick(e);
            }
        },
        [withAuthentication, isSignedIn, onClick, dispatch],
    );
    return (
        <Button
            style={{ ...styles.mainButton, ...customStyle }}
            onClick={handleOnClick}
            disabled={disabled || loading}
            sx={{
                border: `1px solid ${theme[themeStyle].highlight}`,
                backgroundColor:
                    buttonProps?.variant === 'text' || buttonProps?.variant === 'outlined'
                        ? 'none'
                        : disabled
                        ? theme[themeStyle].darkGray
                        : theme[themeStyle].primary_orange,
                '&:hover': {
                    backgroundColor:
                        buttonProps?.variant === 'text' || buttonProps?.variant === 'contained'
                            ? 'none'
                            : theme[themeStyle].deep_grey,
                    border: `1px solid ${theme[themeStyle].highlight}`,
                    color: theme[themeStyle].primary_orange, // stays orange on black background
                    WebkitTextFillColor: theme[themeStyle].primary_orange,
                },
                ...sx,
            }}
            {...buttonProps}>
            {withIcon && beginningIcon && beginningIcon}
            {loading ? (
                <Spinner height={5} width={5} color={theme[themeStyle].black} />
            ) : (
                <Typography variant="body1" style={{ ...styles.mainText, ...textStyle }} sx={textSx}>
                    {label}
                </Typography>
            )}
            {withIcon && customIcon && customIcon}
        </Button>
    );
};

CustomButton.defaultProps = {
    label: 'Button Text',
    onClick: () => {},
};

const dynamicStyles = (colorScheme: ColorScheme, isTextButton?: boolean, isButtonOutline?: boolean) =>
    StyleSheet.create({
        mainButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 5,
            height: '40px',
            paddingBottom: 10,
            paddingTop: 10,
            paddingLeft: 10,
            paddingRight: 10,
            textTransform: 'none',
        },
        text: {
            width: 'fit-content',
            textAlign: 'left',
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
        },
        mainText: {
            width: 'fit-content',
            textAlign: 'left',
            fontSize: 14,
            fontWeight: '600',
            color: isTextButton
                ? theme[colorScheme].highlight
                : isButtonOutline
                ? theme[colorScheme].lightWhite
                : theme[colorScheme].pure_black,
            fontFamily: FONT_ENUM.MAIN_BOLD,
        },
    });
export default CustomButton;
